/**
 * `F5` · `T-50` — Lo que se manda desde la tablet del mostrador.
 *
 * En su propio módulo y no en `inscripcion.ts`: aquello es del **comensal** y
 * esto es del **personal**, y son dos superficies con dos roles y dos riesgos
 * distintos. Mezclarlas invitaría a reutilizar un esquema porque «se parece».
 */
import { z } from 'zod';
/**
 * El PIN: **cuatro dígitos exactos**, y nada más — `Q-20`.
 *
 * > [!danger] Cuatro dígitos son 10 000 combinaciones, y eso es todo
 * > No lo protege su longitud: lo protege que el hash **no lo alcance nadie**
 * > (migración 059) y el bloqueo progresivo por `(tablet, persona)` (060). Si
 * > alguna de esas dos cae, esto es un secreto de trece minutos.
 *
 * Se acota aquí además de en el motor porque es lo que impide gastar 77 ms de
 * `argon2` comprobando una cadena que no puede ser un PIN — y quien llama a
 * este extremo puede hacerlo en bucle.
 */
export const esquemaPin = z.string().regex(/^\d{4}$/);
/**
 * Lo que manda una tablet para convertirse en caja.
 *
 * > [!danger] El teléfono NO viaja aquí, igual que en el alta del comensal
 * > Llega dentro del `token` que Supabase firmó al verificar el SMS. Si viniera
 * > en el cuerpo, cualquiera montaría una caja a nombre de una empleada con
 * > sólo saber su número — y las policies de la 061 tampoco lo dejarían pasar,
 * > porque comparan contra el teléfono del contexto.
 */
export const esquemaAltaDeCaja = z.object({
    /** De qué cafetería es esta caja. Público, y es el mismo del menú. */
    slug: z.string().trim().min(3).max(40),
    /** El de Supabase, de verificar el código. Prueba de que el teléfono es suyo. */
    token: z.string().min(10).max(4096),
    //  > [!info] Sin `etiqueta` desde la 068 — objeción de Isaac, 2026-09-24
    //  El nombre lo pone el MOTOR: `Caja <n> - <sucursal>`. Lo escribía la cajera
    //  y lo tiene que reconocer el dueño en su lista de cajas —y teclearlo exacto
    //  para revocar—, así que el reparto estaba mal hecho. Renombrar llega con
    //  `F6`, en la pantalla donde el nombre importa de verdad.
    /**
     * El PIN que elige, **si todavía no tiene**. Opcional a propósito: quien ya
     * lo eligió alguna vez está montando una caja más, no volviendo a nacer, y
     * pedírselo otra vez sugeriría que puede cambiarlo — que es justo lo que la
     * 059 impide.
     */
    pin: esquemaPin.optional(),
}).strict();
/**
 * `T-70` · Elegir el PIN en una caja **que ya está montada**.
 *
 * La mitad que faltaba. Hasta hoy la única forma de tener PIN era montar una
 * caja, y una tablet montada esconde esa pantalla: la segunda persona de la
 * cafetería veía su nombre en gris y no podía hacer nada.
 *
 * Mismo cuerpo que el alta menos la cookie: `slug` y `token`, porque esta ruta
 * corre **antes** de que exista cualquier sesión de turno —quien llega viene
 * justamente a poder abrirla— y el contexto del motor necesita saber de qué
 * cafetería habla. Lo que acota no es el cuerpo: es el SMS.
 *
 * Y el PIN **no es opcional** aquí, al revés que al montar: montar una tablet
 * sin elegir PIN tiene sentido —lo elige quien la use después—, pero entrar a
 * esta ruta sin PIN no pide nada.
 */
export const esquemaEleccionDePin = z.object({
    slug: z.string().trim().min(3).max(40),
    token: z.string().min(10).max(4096),
    pin: esquemaPin,
}).strict();
/** Lo que contesta: si lo eligió ahora, o si ya tenía uno. */
export const esquemaPinElegido = z.object({
    /**
     * Falso quiere decir «ya tenías uno», y **no es un error**: dos personas
     * pulsando a la vez o un reintento acaban ahí. La pantalla la manda a entrar
     * con el que ya tiene, que es lo útil.
     */
    eligio: z.boolean(),
}).strict();
/** Lo que devuelve el alta: lo justo para pintar la pantalla de turno. */
export const esquemaCajaMontada = z.object({
    /** Cómo quedó llamándose. La compone el motor; aquí sólo se enseña. */
    etiqueta: z.string(),
    sucursal: z.string(),
    /** `true` si acaba de elegir su PIN. `false` si ya lo tenía de antes. */
    eligioPin: z.boolean(),
}).strict();
/**
 * Quién puede entrar en esta caja. **Nombres y nada más.**
 *
 * Ni teléfonos ni identificadores de sucursal: una tablet la mira cualquiera
 * que pase por el mostrador, y para tocar tu nombre en una lista basta el
 * nombre. El identificador sí viaja porque hay que mandarlo de vuelta al
 * entrar, y no dice nada de nadie.
 */
export const esquemaQuienPuedeEntrar = z.object({
    //  > [!danger] El `.strict()` de dentro también, y NO es redundante
    //  `.strict()` no baja a los objetos anidados: sin el de aquí, el de fuera
    //  aceptaba tan campante un `telefono` dentro de cada elemento del arreglo. Lo
    //  cazó su propia prueba, que es para lo que estaba escrita.
    personal: z.array(z.object({
        id: z.uuid(),
        nombre: z.string(),
        /** `false` si todavía no ha elegido PIN: la pantalla la manda a activarse. */
        puedeEntrar: z.boolean(),
    }).strict()),
}).strict();
/** Empezar turno: se toca un nombre y se teclean cuatro dígitos. */
export const esquemaEntrarAlTurno = z.object({
    staffUserId: z.uuid(),
    pin: esquemaPin,
}).strict();
/**
 * `R-09` · Cuánto dura un turno sin tocar nada.
 *
 * Cinco minutos, y es de la precisión aprobada el 2026-08-18: «una caja es un
 * dispositivo semipúblico». No es la duración del turno —eso son horas— sino
 * la del descuido: el tiempo que la pantalla puede quedarse abierta con la
 * sesión de alguien mientras esa persona atiende una mesa.
 */
export const INACTIVIDAD_DEL_TURNO_SEGUNDOS = 5 * 60;
/** Lo que vuelve al entrar. Sin `tenantId` ni nada que no se pinte. */
export const esquemaTurnoAbierto = z.object({
    nombre: z.string(),
    sucursal: z.string(),
}).strict();
/**
 * `T-53` · Lo que la caja manda del campo único: **lo que haya, tal cual**.
 *
 * Sin `tipo`, y es deliberado. Si el cliente mandara «esto es un teléfono», la
 * pantalla tendría que decidirlo —o sea, un modo— y `Flujo-de-mostrador` lo
 * prohíbe: «un campo con el foco puesto, siempre, y la cajera no cambia de modo
 * nunca». Quién decide qué es, decide una vez y en un solo sitio:
 * `interpretarEntradaDeMostrador`.
 */
export const esquemaBusquedaEnLaCaja = z.object({
    //  64 es holgado para lo que puede llegar —diez caracteres o un teléfono
    //  escrito con espacios— y corto para lo que no debería.
    entrada: z.string().min(1).max(64),
}).strict();
/**
 * La ficha de quien está delante, tal como la pinta la caja.
 *
 * `Ficha` y no `Tarjeta`: la tarjeta es la del COMENSAL —la que ve en su
 * teléfono, en `inscripcion.ts`— y son dos cosas distintas. Ésta lleva lo que la
 * cajera necesita para confirmar y para actuar; aquélla, lo que el cliente ve
 * de su propio saldo.
 *
 * > [!danger] Aquí NO viaja el teléfono, y es toda la tarea
 * > Confirmado por Isaac el 2026-09-22: **el teléfono es entrada y nunca
 * > salida.** El nombre sí, porque sin él la cajera no puede confirmar a quién
 * > tiene delante; el número no vuelve nunca, ni siquiera el que ella acaba de
 * > teclear. Lo sostiene el motor —el mostrador no tiene `select` sobre
 * > `customers.phone`— y esto es su reflejo en el contrato.
 */
export const esquemaFicha = z.object({
    customerId: z.uuid(),
    nombre: z.string(),
    /**
     * `false` si es clienta del negocio pero **no tiene tarjeta en esta
     * sucursal**. No es un fallo: con el saldo compartido encendido llega con sus
     * sellos, y sin él empieza aquí. La tarjeta de esta sucursal la abre el primer
     * sello, que es de `T-54`, para que la cajera dé un toque y no dos.
     */
    tieneTarjetaAqui: z.boolean(),
    membershipId: z.uuid().nullable(),
    sellos: z.number().int(),
    /** Nulo si la cafetería todavía no tiene programa vigente. */
    sellosPorPremio: z.number().int().positive().nullable(),
    premios: z.number().int().nonnegative(),
    /**
     * Cuál se entrega si se canjea ahora: **el que vence antes**, y los que no
     * vencen al final. Nulo si no tiene ninguno canjeable. La decisión es del
     * motor y no de la pantalla — con el orden contrario, alguien con dos premios
     * perdería el que le corría prisa.
     */
    premioId: z.uuid().nullable(),
    /**
     * Y **qué** es, para la pantalla de confirmación que `R-09` exige antes de
     * canjear: «vas a entregar un café a Camila». Sale de la fila del premio y no
     * del programa —se copió al alcanzarlo— así que cambiar el programa mañana no
     * cambia lo que se le prometió.
     */
    premioTexto: z.string().nullable(),
    /** `R-05` · El que vence antes, para que la cajera pueda decírselo. */
    premioVenceEn: z.iso.datetime().nullable(),
    /** `R-02` · Nulo quiere decir «ahora mismo», que es el caso de siempre. */
    puedeSellarDesde: z.iso.datetime().nullable(),
    /**
     * `R-09` · Por dónde llegó. El camino del teléfono es el degradado —nadie
     * demostró tener el aparato en la mano— y la pantalla tiene que decirlo antes
     * de un canje. `T-54` lo escribe en `rewards.sin_verificacion`.
     */
}).strict();
/**
 * La misma tarjeta **más por dónde llegó**, que es lo que devuelve buscar.
 *
 * Separado porque sellar y canjear devuelven la tarjeta como quedó, y ahí el
 * camino no cambia: lo sabe la pantalla desde que la encontró. Pedirle al motor
 * que lo repita sería un dato que puede desincronizarse con el de al lado.
 */
export const esquemaTarjetaEnLaCaja = esquemaFicha.extend({
    /**
     * `R-09` · Por dónde llegó. El camino del teléfono es el degradado —nadie
     * demostró tener el aparato en la mano— y la pantalla tiene que decirlo antes
     * de un canje.
     */
    comoLaEncontre: z.enum(['codigo', 'telefono']),
}).strict();
// ── `T-54` · Sellar y canjear ─────────────────────────────────────────────
/** Sellar: a quién. Y nada más — el resto lo pone el motor. */
export const esquemaSello = z.object({ customerId: z.uuid() }).strict();
/**
 * Canjear: qué premio, **el PIN siempre**, y el código si el comensal lo enseñó.
 *
 * `R-09`, precisión aprobada el 2026-08-18: «el canje sí exige teclear el PIN,
 * siempre». No está en el presupuesto de cinco segundos y es la única operación
 * con valor económico que ejecuta el eslabón más rotativo del negocio.
 *
 * El `codigo` es opcional y **no es una declaración de quien llama**: el motor
 * lo intenta gastar y decide la marca «sin verificación del cliente» según haya
 * podido o no. Mandar uno inventado no verifica nada.
 */
export const esquemaCanje = z.object({
    rewardId: z.uuid(),
    pin: esquemaPin,
    codigo: z.string().min(1).max(64).optional(),
}).strict();
/** Lo que vuelve de sellar: la tarjeta como quedó, y si nació un premio. */
export const esquemaSelloHecho = z.object({
    ficha: esquemaFicha,
    premiosNuevos: z.number().int().nonnegative(),
}).strict();
/** Y de canjear: la tarjeta, y si quedó marcado como sin verificar. */
export const esquemaCanjeHecho = z.object({
    ficha: esquemaFicha,
    sinVerificar: z.boolean(),
}).strict();
//# sourceMappingURL=mostrador.js.map